import * as SplashScreen from 'expo-splash-screen';
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Platform, StatusBar, StyleSheet } from 'react-native';

import { tID } from 'app/services/TestHelper';

// splash.png's map background colour, also the Android native splash background (app.config.js)
const backgroundColor = '#f9f9f9';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const splashImage = require('app/assets/images/splash.png');

// Remove the native splash (flat background colour) instantly instead of fading it out over the
// overlay, so the map and logo appear together at once like the iOS splash.
// useBootstrap keeps the native splash up until the overlay below has its image ready.
if (Platform.OS === 'android') SplashScreen.setOptions({ duration: 0 });

export type AndroidSplashOverlayProps = {
  /** Fades the overlay out once the app has mounted its first screen. */
  isReady: boolean;
};

/**
 * Android 12+ native splash screens can only show an icon in a circle on a flat colour.
 * This overlay shows the full-screen splash image (like iOS) above the app while it boots:
 * it hides the native splash once the image has loaded and fades out when the app is ready.
 */
export default function AndroidSplashOverlay({ isReady }: AndroidSplashOverlayProps) {
  if (Platform.OS !== 'android') return null;
  return <Overlay isReady={isReady} />;
}

function Overlay({ isReady }: AndroidSplashOverlayProps) {
  const opacity = useRef(new Animated.Value(1)).current;
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const onImageReady = () => {
    setIsLoaded(true);
    SplashScreen.hideAsync();
  };

  useEffect(() => {
    if (!isReady || !isLoaded) return;
    Animated.timing(opacity, {
      toValue: 0,
      duration: 250,
      useNativeDriver: process.env.NODE_ENV !== 'test',
    }).start(() => setIsHidden(true));
  }, [isReady, isLoaded, opacity]);

  if (isHidden) return null;

  return (
    <>
      <Animated.Image
        // no fade-in: the image replaces the native splash at once
        fadeDuration={0}
        pointerEvents="none"
        resizeMode="cover"
        source={splashImage}
        style={[
          StyleSheet.absoluteFill,
          { width: '100%', height: '100%', backgroundColor, opacity },
        ]}
        testID={tID('AndroidSplashOverlay')}
        onError={onImageReady}
        onLoad={onImageReady}
      />
      <StatusBar barStyle="dark-content" />
    </>
  );
}
